import { Button } from '@cloudscape-design/components';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

function getScrollTop() {
  // Most reliable across browsers
  return (
    window.scrollY
    || document.documentElement.scrollTop
    || document.body.scrollTop
    || 0
  );
}

export default function BackToTopProvider({
  children,
  // show button after user scrolls past this many px
  threshold = 120,
}) {
  const [show, setShow] = useState(false);

  const shouldShow = useMemo(() => (scrollTop) => scrollTop > threshold, [threshold]);

  useEffect(() => {
    let rafId = null;

    const onScroll = () => {
      // throttle to animation frames to avoid spamming setState
      if (rafId) return;

      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        const top = getScrollTop();
        setShow((prev) => {
          const next = shouldShow(top);
          return prev === next ? prev : next;
        });
      });
    };

    // run once on mount (handles refresh on a scrolled position)
    onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
    };
  }, [shouldShow]);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {children}

      {show ? (
        <div
          style={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            zIndex: 9999,
          }}
        >
          <Button
            variant="normal"
            iconName="angle-up"
            onClick={handleClick}
            ariaLabel="Scroll to top of page"
          />
        </div>
      ) : null}
    </>
  );
}
BackToTopProvider.propTypes = {
  children: PropTypes.node.isRequired,
  threshold: PropTypes.number,
};
